import type { SpeechQuestion } from '@/features/nectar/api/models'
import { getSpeechQuestions, submitSpeechAnswer } from '@/features/nectar/api/proficiency-assessment'
import { formatTime } from '@/features/nectar/components/audio-recording/AudioRecordingTask'
import { RecordingStateIdle } from '@/features/nectar/components/audio-recording/RecordingStateIdle'
import { RecordingStateRecording } from '@/features/nectar/components/audio-recording/RecordingStateRecording'
import { RecordingStateRecorded } from '@/features/nectar/components/audio-recording/RecordingStateRecorded'
import { useAudioRecorder } from '@/features/nectar/hooks/useAudioRecorder'
import { Button, Spinner, Text } from '@/ui-kit'
import { useWavesurfer } from '@wavesurfer/react'
import clsx from 'clsx'
import { Pause, Play } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const MIN_RECORDING_SEC = 10
const MAX_RECORDING_SEC = 60

type PhaseSpeechRecordingProps = {
  assessmentId: string
  onAllRecorded: () => void
}

const QuestionAudioPlayer = ({ audioUrl }: { audioUrl: string }) => {
  const playerRef = useRef<HTMLDivElement>(null)
  const { wavesurfer, isPlaying } = useWavesurfer({
    container: playerRef,
    url: audioUrl,
    cursorColor: 'transparent',
    cursorWidth: 2,
    dragToSeek: false,
    height: 40,
    waveColor: '#727272',
    progressColor: '#39c900',
    autoplay: false,
    barWidth: 3,
    barHeight: 4
  })

  return (
    <div className="flex w-full items-center gap-2">
      <Button
        onClick={() => wavesurfer?.playPause()}
        variant="tertiary"
        size="small"
        className="hover:bg-transparent! hover:text-green! flex w-[80px] shrink-0 items-center gap-1"
      >
        {isPlaying ? (
          <>
            <Pause className="h-4 w-4 shrink-0" /> Pause
          </>
        ) : (
          <>
            <Play className="h-4 w-4 shrink-0" /> Play
          </>
        )}
      </Button>
      <div className="h-10 w-full flex-1" ref={playerRef} />
    </div>
  )
}

export const PhaseSpeechRecording = ({ assessmentId, onAllRecorded }: PhaseSpeechRecordingProps) => {
  const [questions, setQuestions] = useState<SpeechQuestion[]>([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [loading, setLoading] = useState(true)

  const { state, seconds, audioUrl, blob, startRecording, stopRecording, resetToIdle, markSubmitting, markRecorded } =
    useAudioRecorder()
  const secondsRef = useRef(seconds)
  secondsRef.current = seconds

  // Fetch speech questions
  useEffect(() => {
    let cancelled = false
    getSpeechQuestions(assessmentId)
      .then((data) => {
        if (!cancelled) {
          setQuestions(data)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load speech questions')
      })
    return () => {
      cancelled = true
    }
  }, [assessmentId])

  const submit = useCallback(async () => {
    if (!blob) return
    if (secondsRef.current < MIN_RECORDING_SEC) {
      toast.error(`Recording must be at least ${MIN_RECORDING_SEC} seconds`)
      return
    }
    const question = questions[currentIdx]
    markSubmitting()

    try {
      const ext = blob.type.includes('webm') ? 'webm' : 'ogg'
      const file = new File([blob], `recording.${ext}`, { type: blob.type })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('questionId', question.id)

      await submitSpeechAnswer(assessmentId, formData)
      toast.success(`Recording ${currentIdx + 1}/${questions.length} uploaded`)

      if (currentIdx < questions.length - 1) {
        setTimeout(() => {
          setCurrentIdx((i) => i + 1)
          resetToIdle()
          // After reset, go back to idle by starting fresh
        }, 400)
      } else {
        setTimeout(() => onAllRecorded(), 400)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload recording')
      markRecorded()
    }
  }, [blob, questions, currentIdx, assessmentId, markSubmitting, resetToIdle, markRecorded, onAllRecorded])

  if (loading) {
    return (
      <div className="flex w-full justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <Text type="body2-regular" className="text-gray-80">
            Loading speech questions...
          </Text>
        </div>
      </div>
    )
  }

  if (!questions.length) {
    return (
      <div className="flex w-full justify-center py-12">
        <Text type="body2-regular" className="text-gray-80">
          No speech questions available.
        </Text>
      </div>
    )
  }

  const currentQuestion = questions[currentIdx]
  const tooShort = seconds < MIN_RECORDING_SEC
  const pastSoftMax = seconds >= MAX_RECORDING_SEC

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col gap-5"
      style={{ animation: 'nectar-slide-up 0.3s ease-out' }}
    >
      {/* Progress */}
      <div className="flex items-center justify-between">
        <Text type="body2-regular" className="text-gray-80">
          Speech {currentIdx + 1} of {questions.length}
        </Text>
        <div className="flex gap-1">
          {questions.map((sq, i) => (
            <div
              key={sq.id}
              className={clsx(
                'size-2 rounded-full',
                i < currentIdx ? 'bg-green' : i === currentIdx ? 'bg-blue' : 'bg-gray-100'
              )}
            />
          ))}
        </div>
      </div>

      {/* Card */}
      <div className="flex flex-col gap-5 rounded-2xl border border-solid border-gray-100 px-6 py-5">
        <div className="flex flex-col gap-1">
          <Text type="body1-regular">Listen and respond</Text>
          <Text type="body3-regular" className="text-gray-80">
            {currentQuestion.prompt || 'Play the audio below, then tap the mic to record your answer.'}
          </Text>
        </div>

        {/* Question audio */}
        <QuestionAudioPlayer key={currentQuestion.id} audioUrl={currentQuestion.audioUrl} />

        {/* Recording timer hints */}
        {state === 'recording' && (
          <div className="flex flex-col items-center gap-1">
            <Text type="body2-regular" className="font-mono text-gray-90">
              {formatTime(seconds)}
            </Text>
            {pastSoftMax ? (
              <Text type="body3-regular" className="text-red">
                You can stop now
              </Text>
            ) : (
              <Text type="body3-regular" className="text-gray-80">
                {MAX_RECORDING_SEC - seconds}s left
              </Text>
            )}
          </div>
        )}

        {/* Recording controls */}
        <div className="flex w-full flex-col items-center gap-5">
          {state === 'idle' && <RecordingStateIdle onStartRecording={startRecording} />}
          {state === 'recording' && <RecordingStateRecording seconds={seconds} onStopRecording={stopRecording} />}
          {(state === 'recorded' || state === 'submitting' || state === 'submitted') && (
            <>
              <RecordingStateRecorded
                audioUrl={audioUrl}
                seconds={seconds}
                isSubmitting={state === 'submitting'}
                onReRecord={() => {
                  resetToIdle()
                  startRecording()
                }}
                onSubmit={submit}
              />
              {tooShort && state !== 'submitting' && (
                <Text type="body3-regular" className="text-red">
                  Recording must be at least {MIN_RECORDING_SEC} seconds
                </Text>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
